import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Package, Sparkles } from "lucide-react";
import { KineticLine, Reveal, Overline } from "@/components/Kinetic";
import { IMAGES } from "@/data/site";

const RANGE = [
  {
    title: "Dry Fruits & Nuts",
    tagline: "Natural goodness. Premium quality. Global choice.",
    image: "/images/dryfruits.jpg",
    items: ["Almonds", "Cashews", "Pistachios", "Walnuts", "Raisins", "Dates", "Mixed Nuts"],
    note: "Handpicked from premium orchards, cleaned, graded and packed under stringent quality checks.",
  },
  {
    title: "Foxnuts (Makhana)",
    tagline: "Nature's crunch. Harvested from Indian ponds.",
    image: "/images/foxnuts.jpg",
    items: ["10–12 Suta", "8–10 Suta", "6–8 Suta", "6 Suta & Below", "Roasted & Salted", "Flavoured Range", "Makhana Flour"],
    note: "Popped, graded and packed at our New Delhi unit — light, protein-rich and gluten-free.",
  },
];

const PACKS = [
  { name: "Stand-Up Pouches", spec: "250g · 500g · 1kg", note: "Zip-lock, freshness sealed" },
  { name: "Glass & PET Jars", spec: "200g – 1kg", note: "Premium shelf presence" },
  { name: "Tin Cans", spec: "250g – 500g", note: "Food-grade, gifting-ready" },
  { name: "Gift Boxes", spec: "Festive & corporate", note: "Custom sleeves & branding" },
  { name: "Bulk Packs", spec: "5kg – 25kg", note: "For trade & HoReCa supply" },
  { name: "Private Label", spec: "Your brand, our craft", note: "Full custom OEM packaging" },
];

const POSITIONING = [
  { title: "Premium Retail", desc: "Shelf-ready packs designed to win the modern trade aisle." },
  { title: "Health & Wellness", desc: "Protein-rich, gluten-free snacking for conscious consumers." },
  { title: "Gifting", desc: "Festive and corporate formats that feel truly premium." },
  { title: "Private Label", desc: "We process, you brand — end-to-end OEM programs." },
];

export default function DryVeda() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <main data-testid="dryveda-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[80vh] items-end overflow-hidden" data-testid="dryveda-hero">
        <motion.img
          src={IMAGES.dryFruitsWide}
          alt="DryVeda premium dry fruits"
          style={{ y: imgY, scale: 1.1 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-forest/75" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-forest to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-40 lg:px-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <Overline>A Nishmayy Global Brand · Processed in New Delhi</Overline>
          </motion.div>
          <h1 className="mt-6 font-serif font-light leading-[0.95] tracking-tight text-cream" data-testid="dryveda-heading">
            <KineticLine delay={0.3} className="text-6xl md:text-7xl lg:text-8xl">
              DryVeda<span className="text-gold">.</span>
            </KineticLine>
          </h1>
          <Reveal delay={0.85} className="mt-8 max-w-xl">
            <p className="text-base font-light leading-relaxed text-sage md:text-lg" data-testid="dryveda-subtext">
              Premium processed dry fruits and foxnuts — graded, flavoured and packed for modern
              retail, gifting and global shelves.
            </p>
          </Reveal>
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="border-b border-forest-border bg-forest-surface py-28" data-testid="dryveda-story">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <Overline>The Story Behind the Name</Overline>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
                Rooted in Vedic wisdom<span className="text-gold">.</span>
              </h2>
            </Reveal>
            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <p className="font-serif text-2xl font-light leading-relaxed text-cream md:text-3xl">
                  DryVeda is inspired by the timeless wisdom of Vedic India, where food was regarded
                  not merely as nourishment, but as a source of purity, balance and well-being.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-8 max-w-3xl text-base font-light leading-relaxed text-sage md:text-lg">
                  The name DryVeda brings together <span className="text-gold">"Dry"</span> — representing
                  our focus on naturally dried foods — and <span className="text-gold">"Veda"</span>, meaning
                  knowledge and wisdom. It reflects our belief that the ancient Vedic approach to food —
                  pure, natural, mindful and close to its original form — remains relevant even today.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <blockquote className="mt-10 border-l-2 border-gold pl-6" data-testid="dryveda-annam-brahma">
                  <p className="font-serif text-2xl font-light italic leading-relaxed text-cream md:text-3xl">
                    "Annam Brahma"
                  </p>
                  <p className="mt-3 text-sm font-light leading-relaxed text-sage md:text-base">
                    In ancient Indian tradition, food was considered sacred — recognized as a fundamental
                    source of life and energy. DryVeda carries this philosophy forward by preserving the
                    natural goodness of food through simple, thoughtful processes.
                  </p>
                </blockquote>
              </Reveal>
              <Reveal delay={0.4}>
                <p className="mt-10 text-xs uppercase tracking-[0.3em] text-gold" data-testid="dryveda-philosophy">
                  Rooted in Vedic wisdom · Preserving nature's purity · Nourishing modern lives
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* THE RANGE */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="dryveda-range">
        <Reveal><Overline>The DryVeda Range</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-serif text-4xl font-light text-cream md:text-5xl">
            Two lines. One standard<span className="text-gold">.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          {RANGE.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.12}>
              <article className="group border border-forest-border bg-forest-surface" data-testid={`dryveda-range-${r.title.includes("Fox") ? "foxnuts" : "dryfruits"}`}>
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={r.image}
                    alt={r.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-surface via-transparent to-transparent" />
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-3xl font-light text-cream">{r.title}</h3>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-gold/90">{r.tagline}</p>
                  <p className="mt-4 text-sm font-light leading-relaxed text-sage">{r.note}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {r.items.map((v) => (
                      <span key={v} className="border border-gold/30 bg-forest px-3.5 py-1.5 text-xs tracking-wide text-cream transition-colors duration-300 hover:border-gold hover:text-gold">
                        {v}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PACK SIZES */}
      <section className="border-y border-forest-border bg-forest-surface py-28" data-testid="dryveda-packs">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><Overline>Pack Sizes & Formats</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
              Packed for every shelf, basket and celebration
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px border border-forest-border bg-forest-border sm:grid-cols-2 lg:grid-cols-3">
            {PACKS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.06} className="bg-forest-surface">
                <div className="group h-full px-8 py-10 transition-colors duration-500 hover:bg-forest-light" data-testid={`dryveda-pack-${i}`}>
                  <Package className="h-6 w-6 text-gold/70" strokeWidth={1.25} />
                  <h3 className="mt-4 text-lg font-semibold tracking-wide text-cream">{p.name}</h3>
                  <p className="mt-1 text-sm text-gold/90">{p.spec}</p>
                  <p className="mt-2 text-sm font-light text-sage">{p.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RETAIL POSITIONING */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="dryveda-positioning">
        <Reveal><Overline>Retail Positioning</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-serif text-4xl font-light text-cream md:text-5xl">
            Built for the modern shelf
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px border border-forest-border bg-forest-border sm:grid-cols-2 lg:grid-cols-4">
          {POSITIONING.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08} className="bg-forest">
              <div className="group h-full px-8 py-12 transition-colors duration-500 hover:bg-forest-light" data-testid={`dryveda-position-${i}`}>
                <Sparkles className="h-6 w-6 text-gold" strokeWidth={1.25} />
                <h3 className="mt-6 font-serif text-2xl font-light text-cream">{p.title}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-sage">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-forest-border" data-testid="dryveda-cta">
        <img src={IMAGES.dryFruitsJar} alt="DryVeda packed dry fruits" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-forest/85" />
        <div className="relative z-10 mx-auto flex max-w-7xl flex-col items-start gap-10 px-6 py-28 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <div>
            <Reveal><Overline>Stock DryVeda</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
                Retail programs, private label<br />and bulk supply<span className="text-gold">.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="dryveda-pricing-button"
                className="group flex items-center gap-3 bg-gold px-10 py-5 text-sm font-semibold tracking-wide text-forest transition-colors duration-300 hover:bg-cream"
              >
                Request Pricing
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href="/catalogue/nishmayy-global-catalogue.pdf"
                download
                data-testid="dryveda-catalogue-button"
                className="group flex items-center gap-3 border border-cream/30 px-10 py-5 text-sm tracking-wide text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                Download Catalogue
              </a>
              <a
                href="/catalogue/dryveda-story-hindi.pdf"
                download
                data-testid="dryveda-hindi-button"
                className="group flex items-center gap-3 border border-cream/30 px-10 py-5 text-sm tracking-wide text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                हिंदी में ड्रायवेदा कहानी
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
